/**
 * What a request with a body has to carry, computed in one place.
 *
 * The API is a Lambda function URL behind CloudFront origin access control.
 * CloudFront signs the method, the path and the headers it forwards — **not the
 * body** — and Lambda rejects an unsigned payload, so AWS requires the caller
 * to send the body's SHA-256 in `x-amz-content-sha256` (`D74`).
 *
 * That was fixed once, in the core's client, and the SES module's client had
 * its own `fetch`. Every module write in a real deployment failed on a
 * signature mismatch — suppression, domains, credentials — while every read
 * worked, because reads have no body. Locally nothing failed at all: the dev
 * server has no OAC in front of it.
 *
 * So it lives here, in the package the core and every module already import,
 * and `no-restricted-syntax` stops a module reaching for `fetch` directly.
 */

/**
 * Lower-case hex, which is what SigV4 expects.
 *
 * `crypto.subtle` needs a secure context: HTTPS in a deployment, and Caddy's
 * local certificate in development.
 */
export async function payloadHash(body: string): Promise<string> {
	const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(body));
	return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface JsonBody {
	readonly body: string;
	readonly headers: Record<string, string>;
}

/**
 * **Serialises once.** The hash has to be of the exact bytes that are sent, and
 * stringifying separately for the hash and for the body is how the two drift
 * apart under key ordering.
 */
export async function jsonBody(value: unknown): Promise<JsonBody> {
	const body = JSON.stringify(value);
	return {
		body,
		headers: {
			'Content-Type': 'application/json',
			'x-amz-content-sha256': await payloadHash(body),
		},
	};
}
